import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import Modal from '../components/Modal';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, EmptyState, Field, Icon, PageHeader, TableSkeleton,
} from '../components/ui';

// Files — admin-managed static platform documents (FAQ, news, policies, …).
// Stored in a private S3 bucket; metadata in the platform_files table. Agents
// read these read-only via the proxy-tools platform_files_list / _read tools.
// Only .txt and .md are accepted, each needs a short description so agents (and
// admins) know what the file is for.

const ACCEPT = '.txt,.md';
const MAX_KB = 256;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { timeZone: 'UTC', hour12: false }) + ' UTC';
  } catch { return iso; }
}

// "19 Jul 2026", what the table shows; the full UTC stamp from formatDate is the tooltip.
function formatDay(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

const isMarkdown = (name) => (name || '').toLowerCase().endsWith('.md');

// Hand the text to the browser as a file. The content endpoint returns the body as JSON,
// so the download is assembled here rather than linked to.
function saveAs(filename, content, contentType) {
  const blob = new Blob([content ?? ''], { type: contentType || 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'file.txt';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick, not inline: revoking before the download has started can
  // cancel it in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function Files() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [preview, setPreview] = useState(null); // { name, content, contentType }
  const [pending, setPending] = useState(null); // `${id}:view` | `${id}:download` while loading
  const [confirm, confirmDialog] = useConfirm();
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await apiFetch('/api/platform-files');
      if (resp.ok) {
        const data = await resp.json();
        setFiles(data.files || []);
      } else {
        toast.error('Failed to load files');
      }
    } catch {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (f) {
      const lower = f.name.toLowerCase();
      if (!lower.endsWith('.txt') && !lower.endsWith('.md')) {
        toast.error('Only .txt and .md files are allowed');
        e.target.value = '';
        return;
      }
      if (f.size > MAX_KB * 1024) {
        toast.error(`File too large (max ${MAX_KB} KB)`);
        e.target.value = '';
        return;
      }
    }
    setFile(f);
  };

  // The file input is uncontrolled and unmounts with the dialog, so the picked file and
  // the description are reset together whenever it closes: a kept `file` with an empty
  // input would upload something the dialog no longer shows.
  const closeUpload = () => {
    if (uploading) return;
    setUploadOpen(false);
    setFile(null);
    setDescription('');
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Choose a .txt or .md file'); return; }
    if (!description.trim()) { toast.error('A description is required'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('description', description.trim());
      const resp = await apiFetch('/api/platform-files', { method: 'POST', body: form });
      if (resp.ok) {
        toast.success('File uploaded');
        setFile(null);
        setDescription('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setUploadOpen(false);
        load();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const fetchContent = async (row) => {
    const resp = await apiFetch(`/api/platform-files/${row.id}/content`);
    if (!resp.ok) return null;
    return resp.json();
  };

  const openPreview = async (row) => {
    setPending(`${row.id}:view`);
    try {
      const data = await fetchContent(row);
      if (data) {
        setPreview({ name: data.filename, content: data.content, contentType: data.content_type });
      } else {
        toast.error('Failed to load file content');
      }
    } catch {
      toast.error('Failed to load file content');
    } finally {
      setPending(null);
    }
  };

  const download = async (row) => {
    setPending(`${row.id}:download`);
    try {
      const data = await fetchContent(row);
      if (data) saveAs(data.filename || row.filename, data.content, data.content_type);
      else toast.error('Failed to download file');
    } catch {
      toast.error('Failed to download file');
    } finally {
      setPending(null);
    }
  };

  const doDelete = async (row) => {
    if (!await confirm({
      title: `Delete file ${row.filename}?`,
      body: <p>Agents will no longer be able to read it.</p>,
      confirmLabel: 'Delete file',
    })) return;
    const ok = await callApi(
      `/api/platform-files/${row.id}`,
      { method: 'DELETE' },
      { successMsg: 'File deleted', errorMsg: 'Delete failed' },
    );
    if (ok) load();
  };

  const uploadButton = (
    <button type="button" className="btn btn-primary" onClick={() => setUploadOpen(true)}>
      <Icon name="upload" /> Upload file
    </button>
  );

  // The skeleton only stands in for a list that has never loaded; a reload after an
  // upload or delete keeps the current rows on screen instead of blanking the table.
  const firstLoad = loading && files.length === 0;

  return (
    <div>
      <PageHeader
        description="Documents the platform makes available to every agent, such as FAQs, news and policies. Agents list them by description and read the one they need."
        actions={uploadButton}
      >
        <h1 className="page-title">
          Files
          {!firstLoad && <Badge tone="neutral" size="lg">{files.length}</Badge>}
        </h1>
      </PageHeader>

      <div className="card card-flush">
        {firstLoad ? (
          <TableSkeleton rows={4} cols={5} label="Loading files" />
        ) : files.length === 0 ? (
          <EmptyState icon="files" title="No files yet" action={uploadButton}>
            Upload a .txt or .md file with a short description. Agents can then list it and read it on demand.
          </EmptyState>
        ) : (
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Description</th>
                  <th className="num">Size</th>
                  <th>Uploaded</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {files.map(f => (
                  <tr key={f.id}>
                    <td data-label="File">
                      <div className="row gap-2 row-top">
                        <Icon name={isMarkdown(f.filename) ? 'fileText' : 'file'} className="text-muted mt-1" />
                        <div className="min-w-0">
                          <span className="cell-primary break">{f.filename}</span>
                          <span className="cell-sub">#{f.id} · {isMarkdown(f.filename) ? 'Markdown' : 'Plain text'}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label="Description" className="wrap text-2">{f.description}</td>
                    <td data-label="Size" className="num nowrap">{formatSize(f.size_bytes)}</td>
                    <td data-label="Uploaded" className="nowrap">
                      <span className="num" title={formatDate(f.created_at)}>{formatDay(f.created_at)}</span>
                      <span className="cell-sub truncate" style={{ maxWidth: 200 }}>{f.uploaded_by || '—'}</span>
                    </td>
                    <td data-label="Actions">
                      <div className="row gap-1 row-end">
                        <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                aria-label={`View ${f.filename}`} title="View"
                                disabled={pending === `${f.id}:view`}
                                onClick={() => openPreview(f)}>
                          {pending === `${f.id}:view` ? <span className="spinner-sm" /> : <Icon name="eye" />}
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                aria-label={`Download ${f.filename}`} title="Download"
                                disabled={pending === `${f.id}:download`}
                                onClick={() => download(f)}>
                          {pending === `${f.id}:download` ? <span className="spinner-sm" /> : <Icon name="download" />}
                        </button>
                        <button type="button" className="btn btn-danger-soft btn-sm btn-icon"
                                aria-label={`Delete ${f.filename}`} title="Delete"
                                onClick={() => doDelete(f)}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={uploadOpen}
        onClose={closeUpload}
        title="Upload a platform file"
        description="Agents list these files by description and read the contents on demand, so say what the file is for."
      >
        <Field label="File" htmlFor="platform-file" required hint={`A .txt or .md file, up to ${MAX_KB} KB.`}>
          <input
            id="platform-file"
            type="file"
            accept={ACCEPT}
            ref={fileInputRef}
            className="form-input"
            onChange={onPickFile}
          />
        </Field>
        <Field label="Description" htmlFor="platform-file-description" required className="mb-0">
          <textarea
            id="platform-file-description"
            className="form-input"
            rows={3}
            placeholder="e.g. Frequently asked questions about billing and subscriptions"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={closeUpload} disabled={uploading}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={uploading || !file || !description.trim()}
          >
            {uploading ? <><span className="spinner-sm" /> Uploading…</> : <><Icon name="upload" /> Upload file</>}
          </button>
        </div>
      </Modal>

      <Modal open={!!preview} onClose={() => setPreview(null)} title={preview ? preview.name : ''} maxWidth={720}>
        {preview && (
          <>
            <pre className="code-block pre-wrap break m-0" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {preview.content}
            </pre>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary"
                      onClick={() => saveAs(preview.name, preview.content, preview.contentType)}>
                <Icon name="download" /> Download
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setPreview(null)}>Close</button>
            </div>
          </>
        )}
      </Modal>

      {confirmDialog}
    </div>
  );
}
